import { v4 as uuidV4 } from "uuid";
import Vue from "vue";
import EventLogicActionsList from "@/EventLogic/components/EventLogicActionsList.vue";
import EventLogicConditionsList from "@/EventLogic/components/EventLogicConditionsList.vue";

export default class Feedback {
  constructor(scenarios) {
    this.originalScenarios = scenarios;
    this.scenarios = this.deepClone(scenarios);
    // Transient UI state (not persisted) for the "move condition" flow:
    // the condition currently picked up, waiting for a destination group.
    this.movingCondition = null;
    // Briefly set to the condition that just landed in a new group, so it
    // can be highlighted; cleared automatically after the highlight fades.
    this.recentlyMovedCondition = null;
  }

  deepClone(scenarios) {
    return JSON.parse(JSON.stringify(scenarios));
  }

  getComponent(name) {
    return EventLogicActionsList.components[name]
      || EventLogicConditionsList.components[name];
  }

  isModified() {
    return JSON.stringify(this.scenarios) !== JSON.stringify(this.originalScenarios);
  }

  prepareScenarios() {
    // return;

    // The UI should always have at least one logicset, and each logicset should have at least one
    // group, which should have at least one if/then/otherwise.
    // (Though initially they'll be 'empty' with just a dropdown to select the action/condition
    // type).
    //
    // When we serialize (i.e. in the cleanupScenarios filter) we remove those empty objects.
    if (!this.scenarios || this.scenarios.length === 0) {
      this.addScenario();
    }
    this.scenarios.forEach(scenario => {
      if (!scenario.name) {
        Vue.set(scenario, 'name', '');
      }
      if (!scenario.id) {
        Vue.set(scenario, 'id', uuidV4());
      }
      this.migrateLegacyGroups(scenario);
      this.migrateGroupModifiers(scenario);
      if (!scenario.groupOperator) {
        Vue.set(scenario, 'groupOperator', 'and');
      }
      if (!scenario.groups || scenario.groups.length === 0) {
        Vue.set(scenario, 'groups', []);
        this.addGroup(scenario);
      }
      scenario.groups.forEach(group => {
        if (!group.id) {
          Vue.set(group, 'id', uuidV4());
        }
        if (!group.conditions || group.conditions.length === 0) {
          this.addCondition(group);
        }
        const max = Math.max(group.conditions.length, 1);
        if (!group.minTrue || group.minTrue > max) {
          Vue.set(group, 'minTrue', Math.min(group.minTrue || 1, max));
        }
      });
      if (!scenario.then || scenario.then.length === 0) {
        this.addAction(scenario, 'then');
      }
      if (!scenario.otherwise || scenario.otherwise.length === 0) {
        this.addAction(scenario, 'otherwise');
      }
    });
  }

  // Converts a scenario saved under the old flat shape (conditionmodifier +
  // numquestions + a single conditions array) into the nested-groups shape.
  // No-ops if the scenario already has groups.
  migrateLegacyGroups(scenario) {
    if (scenario.groups) {
      return;
    }
    const conditions = scenario.conditions || [];
    let minTrue = 1;
    if (scenario.conditionmodifier === 'all') {
      minTrue = Math.max(conditions.length, 1);
    } else if (scenario.conditionmodifier === 'morethan') {
      minTrue = (scenario.numquestions || 0) + 1;
    }
    Vue.set(scenario, 'groups', [{id: uuidV4(), minTrue, conditions}]);
    Vue.set(scenario, 'groupOperator', 'and');
    delete scenario.conditions;
    delete scenario.conditionmodifier;
    delete scenario.numquestions;
  }

  // Converts a scenario saved under the serialized shape (a scenario-level
  // conditionmodifier for combining groups, and per-group conditionmodifier +
  // numquestions instead of minTrue) into the shape the UI edits internally
  // (groupOperator + group.minTrue). No-ops for fields already in the
  // internal shape.
  migrateGroupModifiers(scenario) {
    if (scenario.hasOwnProperty('conditionmodifier') && !scenario.groupOperator) {
      Vue.set(scenario, 'groupOperator', scenario.conditionmodifier === 'any' ? 'any' : 'and');
    }
    delete scenario.conditionmodifier;

    (scenario.groups || []).forEach(group => {
      if (group.hasOwnProperty('conditionmodifier') && !group.hasOwnProperty('minTrue')) {
        const conditions = group.conditions || [];
        let minTrue = 1;
        if (group.conditionmodifier === 'all') {
          minTrue = Math.max(conditions.length, 1);
        } else if (group.conditionmodifier === 'morethan') {
          minTrue = (group.numquestions || 0) + 1;
        }
        Vue.set(group, 'minTrue', minTrue);
      }
      delete group.conditionmodifier;
      delete group.numquestions;
    });
  }

  addScenario() {
    if (!this.scenarios) {
      this.scenarios = [];
    }
    this.scenarios.push({
      name: '',
      id: uuidV4(),
      groupOperator: 'and',
      groups: [{id: uuidV4(), minTrue: 1, conditions: []}],
      then: [],
      otherwise: []
    });
  }

  copyScenario(idx) {
    const newScenario = this.deepClone(this.scenarios[idx]);
    newScenario.name += ' (Copy)';
    newScenario.id = uuidV4();
    newScenario.groups.forEach(group => {
      group.id = uuidV4();
      group.conditions.forEach(condition => condition.id = uuidV4());
    });
    const updateActionIds = action => {
      action.id = uuidV4();
      if (action.incentivetype === 'message') {
        action.message.forEach(message => message.id = uuidV4());
      }
    };
    newScenario.then.forEach(updateActionIds);
    newScenario.otherwise.forEach(updateActionIds);
    this.scenarios.splice(idx + 1, 0, newScenario);
  }

  removeScenario(idx) {
    this.scenarios.splice(idx, 1);
  }

  addGroup(scenario, after) {
    if (!scenario.groups) {
      scenario.groups = [];
    }
    const idx = (after === undefined || after === null) ? scenario.groups.length - 1 : after;
    scenario.groups.splice(idx + 1, 0, {id: uuidV4(), minTrue: 1, conditions: [{id: uuidV4(), type: ''}]});
  }

  removeGroup(scenario, idx) {
    scenario.groups.splice(idx, 1);
  }

  // Appends a group to a scenario and sets the scenario's groupOperator in
  // the same step, for the "Add AND Group" / "Add OR Group" shortcut buttons
  // shown when a scenario only has one group.
  addGroupToScenario(scenario, groupOperator) {
    this.addGroup(scenario, scenario.groups.length - 1);
    scenario.groupOperator = groupOperator;
  }

  addAction(scenario, location, after) {
    if (!scenario[location]) {
      scenario[location] = [];
    }
    scenario[location].splice(after + 1, 0, {id: uuidV4(), incentivetype: ''});
  }

  addCondition(group, after) {
    if (!group.conditions) {
      group.conditions = [];
    }
    // If the group currently requires all of its conditions ("All"), keep
    // requiring all of them after this one's added - otherwise it silently
    // turns into "at least N of N+1", which reads as a different rule.
    // (A single-condition group's minTrue of 1 is always displayed as "Any",
    // never "All", so it's excluded here.)
    const wasAll = group.conditions.length > 1 && group.minTrue >= group.conditions.length;
    group.conditions.splice(after + 1, 0, {id: uuidV4(), type: ''});
    if (wasAll) {
      group.minTrue = group.conditions.length;
    }
  }

  // Appends a condition to a group and sets its quantifier in the same step,
  // for the "Add AND Condition" / "Add OR Condition" shortcut buttons shown
  // when a group only has one condition.
  addConditionToGroup(group, minTrue) {
    this.addCondition(group, group.conditions.length - 1);
    group.minTrue = minTrue;
  }

  startMovingCondition(condition) {
    this.movingCondition = condition;
  }

  cancelMovingCondition() {
    this.movingCondition = null;
  }

  moveConditionToGroup(targetGroup) {
    if (!this.movingCondition) {
      return;
    }
    const condition = this.movingCondition;
    // Same "All" preservation as addCondition, applied before the condition
    // actually lands in targetGroup.conditions.
    const wasAll = targetGroup.conditions.length > 1 && targetGroup.minTrue >= targetGroup.conditions.length;

    this.scenarios.forEach(scenario => {
      scenario.groups.forEach(group => {
        const idx = group.conditions.indexOf(condition);
        if (idx !== -1) {
          group.conditions.splice(idx, 1);
        }
      });
    });

    targetGroup.conditions.push(condition);
    if (wasAll) {
      targetGroup.minTrue = targetGroup.conditions.length;
    }
    this.movingCondition = null;

    // Briefly highlight the condition that just landed, then clear it -
    // guarded so an overlapping later move's timeout can't clear a newer one.
    this.recentlyMovedCondition = condition;
    setTimeout(() => {
      if (this.recentlyMovedCondition === condition) {
        this.recentlyMovedCondition = null;
      }
    }, 2000);
  }

  cleanupScenarios() {
    // return scenarios;
    let tmp = JSON.parse(JSON.stringify(this.scenarios));

    tmp.forEach(scenario => {
      scenario.conditionmodifier = scenario.groupOperator === 'any' ? 'any' : 'all';
      delete scenario.groupOperator;

      scenario.groups = (scenario.groups || []).map(group => {
        // Get rid of conditions with no type. Submission is already blocked before
        // this ever runs if a fill-in was forgotten (see EventLogicConditionsList's
        // isConditionInvalid/isAlwaysRunCondition), so this is just cleanup of the
        // one case that's intentionally allowed through: a lone blank condition in
        // a scenario's only group, meaning "always run".
        group.conditions = group.conditions.filter(function (condition) {
          return !!condition.type;
        });

        // The condition may only store what it has as props on its component,
        // plus the condition type
        group.conditions.forEach(condition => {
          if (this.getComponent(condition.type)) {
            const props = this.getComponent(condition.type).props || {};
            const acceptableKeys = Object.keys(props).concat('type');

            Object.keys(condition).forEach(key => {
              if (acceptableKeys.indexOf(key) === -1) {
                delete condition[key];
              }
            });
          }
        });

        // Translate the group's quantifier (minTrue, an internal editing
        // concept) into the conditionmodifier/numquestions pair the server
        // expects, mirroring how a scenario's own modifier used to work
        // before groups existed.
        const max = Math.max(group.conditions.length, 1);
        const minTrue = Math.min(Math.max(group.minTrue || 1, 1), max);

        let conditionmodifier = 'any';
        let numquestions = 1;
        if (max > 1 && minTrue >= max) {
          conditionmodifier = 'all';
        } else if (minTrue > 1) {
          conditionmodifier = 'morethan';
          numquestions = minTrue - 1;
        }

        // TODO: group.id is never included here, so it never survives a save - it's purely a
        // client-side/editing concern (Vue :key, drag-and-drop tracking). Remove group ids
        // altogether (prepareScenarios, migrateLegacyGroups, addScenario, addGroup, copyScenario)
        // rather than generating and migrating something the server never sees.
        return {
          conditions: group.conditions,
          conditionmodifier,
          numquestions
        };
      }).filter(group => group.conditions.length > 0);

      ['then', 'otherwise'].forEach(section => {
        // Get rid of actions with no type
        scenario[section] = scenario[section].filter(action => !!action.incentivetype);

        // An action may only store what it has as props on its component, plus the incentivetype
        scenario[section].forEach(action => {
          if (this.getComponent(action.incentivetype)) {
            const props = this.getComponent(action.incentivetype).props || {};

            const acceptableKeys = Object.keys(props).concat('incentivetype');

            Object.keys(action).forEach(function (key) {
              if (acceptableKeys.indexOf(key) === -1) {
                delete action[key];
              }
            });
          }
        });
      });
    });

    // Remove empty scenarios
    tmp = tmp.filter(scenario => {
      return (scenario.then.length > 0
        || scenario.otherwise.length > 0
        || scenario.groups.length > 0
        || scenario.name.length > 0);
    });
    return tmp;
  }

  setupReactiveProps() {
    // Make sure each thing has the reactive props that it needs
    this.scenarios.forEach(scenario => {
      scenario.groups.forEach(group => {
        group.conditions.forEach(condition => {
          this.updateConditionProps(condition);
        });
      });
      scenario.then.forEach(action => {
        this.updateActionProps(action);
      });
      scenario.otherwise.forEach(action => {
        this.updateActionProps(action);
      });
    });
  }

  updateConditionProps(condition) {
    if (!condition.type) {
      // there's no condition type and thus no component to get props from
      return;
    }
    this.updateComponentProps(condition, this.getComponent(condition.type));
  }

  updateActionProps(action) {
    if (!action.incentivetype) {
      // there's no action type and thus no component to get props from
      return;
    }
    // debugger;
    this.updateComponentProps(action, this.getComponent(action.incentivetype));
  }

  updateComponentProps(object, component) {
    const props = component.props || {};
    Object.keys(props).forEach(prop => {
      if (object.hasOwnProperty(prop)) {
        return;
      }
      let newValue = props[prop].hasOwnProperty('default') ? props[prop].default : null;
      if (typeof newValue === 'function') {
        newValue = newValue();
      }
      Vue.set(
        object,
        prop,
        newValue
      );
    });

  }
}
